import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BadgeDollarSign, FileText, Receipt, RefreshCw, Send, Wallet } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Loading,
  SearchInput,
  Select,
  Stat,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { BUCKET_TONE, compactMoney, dateLabel, money, num, titleCase } from '../lib/format.js';

const BUCKETS = [
  { value: 'all', label: 'All balances' },
  { value: 'current', label: 'Current' },
  { value: '1-30', label: '1–30 days' },
  { value: '31-60', label: '31–60 days' },
  { value: '60+', label: '60+ days' },
  { value: 'paid', label: 'Paid' },
];

export default function Invoices() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const [generating, setGenerating] = useState(false);

  const bucket = params.get('bucket') || 'all';
  const status = params.get('status') || 'all';

  const filters = useMemo(
    () => ({ bucket, status, q: debounced || undefined, sort: 'issued', dir: 'desc', limit: 200 }),
    [bucket, status, debounced],
  );

  const { data, meta, loading, refresh } = useApi('/invoices', { params: filters });
  const { data: aging } = useApi('/invoices/aging');
  const invoices = data || [];

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const payload = await api.post('/invoices/generate', {});
      const created = payload.data || [];
      toast.ok(
        `${created.length} invoice${created.length === 1 ? '' : 's'} generated`,
        `${created.reduce((s, i) => s + Number(i.jobsCount || 0), 0)} jobs across ${created.length} customers`,
      );
      refresh();
    } catch (err) {
      toast.err('Could not generate invoices', err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <PageHead
        title="Invoices & money owed"
        subtitle="Bill customers for finished work and keep an eye on who still owes us."
        icon={Receipt}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
            <Button variant="primary" size="sm" icon={FileText} loading={generating} onClick={generate}>
              Make invoices for finished jobs
            </Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Still owed to us" icon={Wallet} value={compactMoney(meta?.summary?.outstanding)} tone="amber" foot={`${num(meta?.summary?.count)} invoices`} />
        <Stat label="Late" icon={BadgeDollarSign} value={compactMoney(meta?.summary?.overdue)} tone={Number(meta?.summary?.overdue) > 0 ? 'red' : 'green'} foot="needs a call" />
        <Stat label="Charged so far" icon={Receipt} value={compactMoney(meta?.summary?.billed)} tone="blue" foot="across this view" />
        <Stat
          label="Usual invoice"
          icon={Wallet}
          value={compactMoney(Number(meta?.summary?.billed) / Math.max(Number(meta?.summary?.count), 1))}
          tone="violet"
          foot="per invoice"
        />
      </div>

      <div className="split mb-20">
        <Card>
          <CardHead title="How late the money is" subtitle="Money owed to us, by how late it is" icon={Wallet} />
          <CardBody>
            <div className="grid grid-4">
              {[
                ['Current', aging?.buckets?.currentBalance, 'green'],
                ['1–30 days', aging?.buckets?.days1to30, 'teal'],
                ['31–60 days', aging?.buckets?.days31to60, 'amber'],
                ['60+ days', aging?.buckets?.days61plus, 'red'],
              ].map(([label, value, tone]) => (
                <div key={label} className="tile" data-tone={tone}>
                  <span className="label">{label}</span>
                  <div className="stat-value sm" style={{ color: 'var(--tone)', fontSize: 'var(--fs-2xl)' }}>
                    {compactMoney(value)}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Who owes the most" subtitle="Latest payers first" icon={BadgeDollarSign} />
          <CardBody className="col gap-10" style={{ maxHeight: 240, overflow: 'auto' }}>
            {(aging?.byCustomer || []).slice(0, 8).map((row) => (
              <Link key={row.id} to={`/app/customers/${row.id}`} className="row between gap-10">
                <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{row.name}</span>
                <span className="row gap-8">
                  {Number(row.overdue) > 0 ? <Badge tone="red" size="sm">{money(row.overdue)} late</Badge> : null}
                  <span className="num dim" style={{ fontSize: 'var(--fs-sm)' }}>{money(row.balance)}</span>
                </span>
              </Link>
            ))}
            {!aging?.byCustomer?.length ? <span className="dim">Nothing outstanding. Nice.</span> : null}
          </CardBody>
        </Card>
      </div>

      <div className="filter-bar">
        <SearchInput value={query} onChange={setQuery} placeholder="Invoice number, customer, PO…" style={{ minWidth: 240, maxWidth: 320 }} />
        <Select className="select-sm" value={bucket} onChange={(e) => setParam('bucket', e.target.value)} options={BUCKETS} />
        <Select
          className="select-sm"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
          options={[
            { value: 'all', label: 'Any status' },
            { value: 'draft', label: 'Draft' },
            { value: 'sent', label: 'Sent' },
            { value: 'partial', label: 'Partially paid' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'paid', label: 'Paid' },
          ]}
        />
        <div className="grow" />
        <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{invoices.length} invoices in this view</span>
      </div>

      {loading && !invoices.length ? (
        <Loading rows={8} />
      ) : invoices.length ? (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th className="narrow">Issued</th>
                  <th className="narrow">Due</th>
                  <th className="narrow">Status</th>
                  <th className="narrow">How late</th>
                  <th className="right">Jobs</th>
                  <th className="right">Total</th>
                  <th className="right">Still owed</th>
                  <th className="narrow right" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={cx('clickable', invoice.status === 'overdue' && 'overdue-row')}
                    onClick={() => navigate(`/app/invoices/${invoice.id}`)}
                  >
                    <td className="mono" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{invoice.code}</td>
                    <td className="truncate">{invoice.customerName}</td>
                    <td className="narrow num">{dateLabel(invoice.issuedAt)}</td>
                    <td className="narrow num">
                      {dateLabel(invoice.dueAt)}
                      {invoice.daysPastDue > 0 ? (
                        <div className="dimmer" style={{ fontSize: 10 }}>{invoice.daysPastDue}d past due</div>
                      ) : null}
                    </td>
                    <td className="narrow">
                      <Badge size="sm" tone={invoice.status === 'paid' ? 'green' : invoice.status === 'overdue' ? 'red' : invoice.status === 'draft' ? 'muted' : 'blue'}>
                        {titleCase(invoice.status)}
                      </Badge>
                    </td>
                    <td className="narrow">
                      <Badge size="sm" tone={BUCKET_TONE[invoice.agingBucket] || 'muted'}>{invoice.agingBucket}</Badge>
                    </td>
                    <td className="right num">{invoice.jobsCount}</td>
                    <td className="right num">{money(invoice.total)}</td>
                    <td className="right num" style={{ fontWeight: 620, color: Number(invoice.balance) > 0 ? 'var(--amber)' : undefined }}>
                      {money(invoice.balance)}
                    </td>
                    <td className="narrow right">
                      {invoice.status === 'draft' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          icon={Send}
                          onClick={async (event) => {
                            event.stopPropagation();
                            try {
                              await api.post(`/invoices/${invoice.id}/send`);
                              toast.ok(`${invoice.code} sent`);
                              refresh();
                            } catch (err) {
                              toast.err('Could not send', err.message);
                            }
                          }}
                        >
                          Send
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState icon={Receipt} title="No invoices in this view">
            Verify completed work, then generate invoices for the period.
          </EmptyState>
        </Card>
      )}
    </>
  );
}
